import { formatChartAxisDateValue } from "./formatChartAxisDate";
import { normalizeColumnKeyForMatch } from "./normalizeTableRows";

const DATE_COLUMN_KEY =
  /^(month|yearmonth|reportmonth|billingmonth|ym|date|orderdate|startdate|enddate|createdat|updatedat)$/i;

/** Columns that must show API text as-is (e.g. fiscal year "2025-26", employee IDs). */
const NON_NUMERIC_COLUMN_KEY =
  /^(fiscalyear|financialyear|fy|finyear|employeeid|empid|employee_id|emp_id|staffid|staff_id|userid|user_id|memberid|member_id|doctorid|doctor_id|customerid|customer_id|id)$/i;

/**
 * True only when the entire value is numeric. Avoids partial parseFloat
 * (e.g. "2025-26" → 2025) and range/label strings shown as numbers.
 */
/** Match table column keys after sort API may rename labels (e.g. "Sales (₹)" vs "Sales"). */
export function sqlSortColumnsMatch(a, b) {
  if (a == null || b == null) return false;
  if (a === b) return true;
  return normalizeColumnKeyForMatch(a) === normalizeColumnKeyForMatch(b);
}

export function isSqlPureNumericValue(value) {
  if (value == null || value === "") return false;
  if (typeof value === "number" && Number.isFinite(value)) return true;
  if (typeof value !== "string") return false;

  let trimmed = String(value).trim();
  if (!trimmed || trimmed === "-") return false;

  trimmed = trimmed.replace(/[%％]\s*$/g, "").trim();
  trimmed = trimmed.replace(/^(?:INR|USD|EUR|GBP|JPY)\s+/i, "").trim();

  const cleaned = trimmed.replace(/[₹$€£¥₽¢¤\u20B9\u00A3\u20AC,\s]/g, "");
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return false;

  return Number.isFinite(parseFloat(cleaned));
}

/** Currency symbols / codes to remove from column names before sort API. */
const CURRENCY_IN_COLUMN_NAME = /[₹$€£¥₽¢¤\u20B9\u00A3\u20AC]|\b(?:INR|USD|EUR|GBP|JPY)\b/gi;

/**
 * Strip currency symbols (and common currency codes) from a column name for API requests.
 */
export function stripCurrencyFromColumnName(columnName) {
  if (columnName == null || columnName === "") return "";
  return String(columnName)
    .replace(CURRENCY_IN_COLUMN_NAME, "")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeColumnKey(col) {
  return String(col ?? "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

export function isSqlDateColumn(col, rawResults) {
  if (DATE_COLUMN_KEY.test(normalizeColumnKey(col))) return true;
  return rawResults?.some((row) => {
    const v = row?.[col];
    return v != null && v !== "" && formatChartAxisDateValue(v) !== v;
  });
}

/** ID / code columns — show API values as-is (no comma grouping). */
export function isSqlIdentifierColumn(col) {
  return NON_NUMERIC_COLUMN_KEY.test(normalizeColumnKey(col));
}

/** Money / amount columns — Indian comma formatting in table cells only. */
export function isSqlAmountColumn(col) {
  if (!col || isSqlIdentifierColumn(col)) return false;

  const raw = String(col);
  if (CURRENCY_IN_COLUMN_NAME.test(raw)) return true;

  const lower = raw.toLowerCase();
  if (/\b(amount|amt|price|cost|revenue|turnover|billing|fee|charges?)\b/.test(lower)) {
    return true;
  }

  const hasMonetaryModifier =
    /\b(total|net|gross|sum|avg|average)\b/.test(lower) ||
    CURRENCY_IN_COLUMN_NAME.test(raw);

  return hasMonetaryModifier && /\b(sales|value|revenue)\b/.test(lower);
}

export function isSqlNumericColumn(col, rawResults) {
  if (isSqlDateColumn(col, rawResults)) return false;
  if (isSqlIdentifierColumn(col)) return false;
  return rawResults?.some((row) => isSqlPureNumericValue(row?.[col]));
}
